import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Platform, type StyleProp, type ViewStyle } from 'react-native';
import { BottomTabBar, type BottomTabItem } from '@cubemeet/ui';
import { bottomItems, getRouteForKey } from '../config/bottomNav';
import { useI18n } from '../i18n';
import useHideOnScroll from '../hooks/useHideOnScroll';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { QuickAddMenu } from '@cubemeet/ui';
import CardFormHost from '@cubemeet/ui/components/cards/CardFormHost';
import { FullscreenPoiModal } from '@cubemeet/ui';
import { useGlobalToast } from '../contexts/ToastContext';
import { useAdmin } from '@cubemeet/core';
// empty payload helpers from module-travel (used to open the full POI editor in create mode)
import { createEmptyPoiPayload } from '../../../../packages/module-travel/src/web/utils/emptyPayloads/poi';
import { createEmptyMealPayload } from '../../../../packages/module-travel/src/web/utils/emptyPayloads/meal';
import { createEmptyLodgingPayload } from '../../../../packages/module-travel/src/web/utils/emptyPayloads/lodging';
import { createEmptyActivityPayload } from '../../../../packages/module-travel/src/web/utils/emptyPayloads/activity';
import { createEmptyShoppingPayload } from '../../../../packages/module-travel/src/web/utils/emptyPayloads/shopping';
import { createEmptyVideoPayload } from '../../../../packages/module-travel/src/web/utils/emptyPayloads/video';
import { createEmptyExternalPayload } from '../../../../packages/module-travel/src/web/utils/emptyPayloads/external';
// PWA-related UI removed: InstallPwaPrompt and PwaDebugPanel
// Note: we will dynamically portal the footer to document.body on web to
// avoid stacking-context issues caused by other overlays.

interface AppLayoutProps {
  children?: React.ReactNode;
  showBottomNav?: boolean;
  activeTabIndex?: number;
  onTabPress?: (index: number, key: string) => void;
}

/**
 * 统一的应用布局组件
 * 包含固定的底部导航栏
 * 页面内容（包括header）通过children传入
 * 
 * ⚠️ 注意：此组件假设用户已认证。认证检查应在路由级别进行。
 */
const AppLayout: React.FC<AppLayoutProps> = ({ 
  children,
  showBottomNav = true,
  activeTabIndex = 0,
  onTabPress,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useI18n();
  
  // Check if current user is admin
  const { isAdmin } = useAdmin();
  const { showSuccess, showError } = useGlobalToast();

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [reactDom, setReactDom] = useState<any>(null);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [cardFormVisible, setCardFormVisible] = useState(false);
  const [cardFormType, setCardFormType] = useState<any>(null);
  const [showPoiModal, setShowPoiModal] = useState(false);
  const [poiPayload, setPoiPayload] = useState<any>(null);
  const [supabaseClient, setSupabaseClient] = useState<any>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // 初始化门户容器
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    try {
      const g: any = (globalThis as any) || {};
      if (g && g.document && g.document.body) {
        let container = g.document.getElementById('app-bottom-nav-portal');
        if (!container) {
          container = g.document.createElement('div');
          container.id = 'app-bottom-nav-portal';
          container.setAttribute('data-bottom-nav-portal','1');
          g.document.body.appendChild(container);
        } else {
          g.document.body.appendChild(container);
          try { container.setAttribute('data-bottom-nav-portal','1'); } catch (e) {}
        }

        setPortalContainer(container);

        (async () => {
          try {
            const rd = await import('react-dom');
            setReactDom(rd);
          } catch (err) {
            // ignore
          }
        })();
      }
    } catch (err) {
      // ignore
    }
  }, []);

  const translateKey = (key?: string) => {
    if (!key || !translations) return undefined;
    const parts = key.split('.');
    let cur: any = translations as any;
    for (const p of parts) {
      if (cur && typeof cur === 'object' && p in cur) {
        cur = cur[p];
      } else {
        return undefined;
      }
    }
    return typeof cur === 'string' ? cur : undefined;
  };

  // Determine active index from current path
  const derivedActiveIndex = React.useMemo(() => {
    const path = location.pathname;
    const idx = bottomItems.findIndex(item => {
      const route = getRouteForKey(item.key);
      if (route === '/' || route === '') return path === '/';
      return path.startsWith(route);
    });
    return idx !== -1 ? idx : activeTabIndex;
  }, [location.pathname, activeTabIndex]);

  // map the exported bottomItems to localized labels (fall back to the
  // provided `label` field if translation is missing)
  const webBottomItems: BottomTabItem[] = bottomItems.map((it) => {
    const origLabel = typeof it.label === 'string' ? it.label : '';
    // If the original label is intentionally empty (e.g. center action), keep it empty
    // to avoid rendering a translated string under the center button.
    if (origLabel === '') {
      return { ...it, label: '' } as BottomTabItem;
    }

    const translated = (it as any).labelKey ? translateKey((it as any).labelKey) ?? origLabel : origLabel;
    return { ...it, label: translated } as BottomTabItem;
  });

  const handleTabPress = (index: number, key: string) => {
    if (onTabPress) {
      onTabPress(index, key);
      return;
    }
    const target = getRouteForKey(key);
    navigate(target);
  };
            {/* PWA debug panel removed */}

  const handleCenterPress = () => {
    // Open a bottom half-sheet modal on web instead of navigating directly
    // This mirrors mobile UX where the center action opens an action sheet.
    if (Platform.OS === 'web') {
      setShowCreateSheet(true);
      return;
    }
    navigate('/travel/create');
  };

  const closeCreateSheet = () => setShowCreateSheet(false);

  const shouldLockScroll = Platform.OS === 'web' && (showCreateSheet || showQuickAdd || !!showPoiModal || cardFormVisible);

  useEffect(() => {
    if (!shouldLockScroll) return;
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const body = document.body;
    if (!body) return;
    const prevCount = Number(body.dataset.scrollLockCount || '0');
    body.dataset.scrollLockCount = String(prevCount + 1);
    if (prevCount === 0) {
      body.dataset.scrollLockPrev = body.style.overflow || '';
      body.style.overflow = 'hidden';
    }
    return () => {
      const currentCount = Number(body.dataset.scrollLockCount || '0');
      const nextCount = Math.max(currentCount - 1, 0);
      body.dataset.scrollLockCount = String(nextCount);
      if (nextCount === 0) {
        const restore = body.dataset.scrollLockPrev || '';
        body.style.overflow = restore;
        delete body.dataset.scrollLockPrev;
      }
    };
  }, [shouldLockScroll]);

  // Do not inject web-only container class; prefer style-based full-width layout
  const contentStyle: StyleProp<ViewStyle> = Platform.OS === 'web' ? [styles.content, styles.contentWeb] : styles.content;

  const footerStyle: StyleProp<ViewStyle> = Platform.OS === 'web' ? [styles.footer, styles.footerWeb] : styles.footer;

  // On web, slightly raise & scale the center tab icon to make it more prominent
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    try {
      const portal = portalContainer || document.getElementById('app-bottom-nav-portal');
      if (!portal) return undefined;
      // find candidate tab items inside the portal/fallback
      const tabs = portal.querySelectorAll('[role="button"], [tabindex="0"], .r-flex-13awgt0');
      if (!tabs || tabs.length === 0) return undefined;
      const centerIndex = Math.floor(tabs.length / 2);
      const center = tabs[centerIndex];
      if (center) {
        // apply inline styles to lift and enlarge the center icon
        (center as HTMLElement).style.transform = 'translateY(-6px) scale(1.08)';
        (center as HTMLElement).style.transition = 'transform 160ms ease';
        // ensure it doesn't overlap by increasing z-index locally
        (center as HTMLElement).style.zIndex = '2147483648';
      }
    } catch (err) {
      // ignore
    }
    return undefined;
  }, [portalContainer]);

  // Listen for cross-page requests to open the create sheet (legacy pages may dispatch this)
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const handler = () => { try { setShowCreateSheet(true); } catch (err) {} };
    window.addEventListener('openCreateSheet', handler as EventListener);
    return () => { window.removeEventListener('openCreateSheet', handler as EventListener); };
  }, []);

  // When the POI modal is shown, dynamically load the supabase client so the
  // modal can perform DB persistence itself. We avoid static imports to keep
  // the web bundle analysis simple and match other pages' dynamic import pattern.
  useEffect(() => {
    let mounted = true;
    if (!showPoiModal) {
      // clear client when modal closed
      try { if (mounted) setSupabaseClient(null); } catch (e) {}
      return () => { mounted = false; };
    }
    (async () => {
      try {
        const mod = await import('../../../../packages/core/src/services/supabase');
        if (mounted && mod && (mod as any).supabase) setSupabaseClient((mod as any).supabase);
      } catch (e) {
        console.debug('AppLayout: failed to load supabase client for POI modal', e);
        if (mounted) setSupabaseClient(null);
      }
    })();
    return () => { mounted = false; };
  }, [showPoiModal]);

  // Load supabase on mount to derive current user id for guarding create flows.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    let mounted = true;
    (async () => {
      try {
        const mod = await import('../../../../packages/core/src/services/supabase');
        const client = mod && (mod as any).supabase;
        if (!mounted || !client) return;
        try {
          // supabase-js v2: auth.getUser()
          if (client.auth && client.auth.getUser) {
            const { data } = await client.auth.getUser();
            if (data && data.user && data.user.id) {
              setCurrentUserId(String(data.user.id));
              return;
            }
          }
        } catch (e) {
          // ignore
        }
        try {
          // fallback: older API client.auth.user()
          if (client.auth && typeof client.auth.user === 'function') {
            const u = client.auth.user();
            if (u && u.id) setCurrentUserId(String(u.id));
          }
        } catch (e) {
          // ignore
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  const { hidden: scrollHidden } = useHideOnScroll({ autoDetect: true, listenOnWindow: true });
  // hide footer when full-screen modal is open OR when user scrolls down
  const footerHidden = !!showPoiModal || scrollHidden; // hide footer when full-screen modal is open or on scroll
  const footerNode = (
    <div style={{
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 2147483647,
      width: '100%',
      // respect safe-area on modern mobile browsers and add small extra offset
      paddingBottom: 'calc(env(safe-area-inset-bottom, 16px) + 14px)',
      paddingTop: '10px',
      boxSizing: 'border-box',
      pointerEvents: footerHidden ? 'none' : 'auto',
      justifyContent: 'center',
      alignItems: 'center',
      transform: footerHidden ? 'translateY(110%)' : 'translateY(0)',
      transition: 'transform 240ms ease',
      backgroundColor: 'rgba(255,255,255,1)',
      borderTop: '1px solid rgba(0,0,0,0.06)',
      boxShadow: '0 -6px 20px rgba(0,0,0,0.06)'
    }}>
      <div style={{ width: '100%', boxSizing: 'border-box' }}>
        <BottomTabBar
          items={webBottomItems}
          activeIndex={derivedActiveIndex}
          onPress={handleTabPress}
          showRaisedCenter
          onCenterPress={handleCenterPress}
        />
      </div>
    </div>
  );

  // Mark internal SPA navigations in sessionStorage so useShowBackButton can
  // distinguish internal navigation from external referrer openings.
  // We skip the initial mount so an external entry won't be marked as internal.
  const initialLocationRef = React.useRef(true);
  React.useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    try {
      if (initialLocationRef.current) {
        initialLocationRef.current = false;
        return undefined;
      }
      try {
        sessionStorage.setItem('cm_last_nav_internal', String(Date.now()));
      } catch (e) {
        // ignore storage errors
      }
    } catch (e) {
      // ignore
    }
    return undefined;
  }, [location.pathname, location.search]);

  return (
    <View style={styles.container}>
      {/* 页面内容（包括header和可滚动区域） */}
      <View style={contentStyle}>
        {/* Render children when AppLayout is used as a wrapper; otherwise render nested routes via Outlet */}
        {typeof children !== 'undefined' && children !== null ? children : <Outlet />}
      </View>

      {/* Bottom half-sheet modal for center action (web only) */}
      {Platform.OS === 'web' && showCreateSheet && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.36)',
            zIndex: 2147483650,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
          onClick={closeCreateSheet}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              width: '100%',
              maxWidth: 600,
              // use auto height so sheet fits content and avoid large empty space
              height: 'auto',
              maxHeight: '60vh',
              background: '#fff',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              boxShadow: '0 -12px 40px rgba(0,0,0,0.18)',
              padding: 8,
              boxSizing: 'border-box',
              transform: 'translateY(0)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
            onClick={(e: any) => e.stopPropagation()}
          >
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', paddingTop: 6 }}>
              <div style={{ width: 40, height: 4, background: '#E5E7EB', borderRadius: 4 }} />
            </div>

            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <button
                style={{ width: '100%', maxWidth: 520, padding: '14px 12px', borderRadius: 10, border: 'none', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 16 }}
                onClick={() => {
                  // creation routes require authentication - guard by checking currentUserId
                  if (!currentUserId) {
                    try { showError && showError(translateKey('navigation.loginToCreate') || '請先登入以建立行程'); } catch (e) {}
                    closeCreateSheet();
                    try {
                      const referer = '/travel/create';
                      navigate(`/auth?referer=${encodeURIComponent(referer)}`);
                    } catch (e) {
                      navigate('/auth');
                    }
                    return;
                  }
                  closeCreateSheet(); navigate('/travel/create');
                }}
              >
                {translateKey('navigation.createTravel') || '建立行程'}
              </button>

              <button
                style={{ width: '100%', maxWidth: 520, padding: '14px 12px', borderRadius: 10, border: 'none', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 16 }}
                onClick={() => {
                  // require login before viewing personal itineraries
                  if (!currentUserId) {
                    try { showError && showError(translateKey('navigation.loginToView') || '請先登入以查看我的行程'); } catch (e) {}
                    closeCreateSheet();
                    try {
                      const referer = '/travel';
                      navigate(`/auth?referer=${encodeURIComponent(referer)}`);
                    } catch (e) {
                      navigate('/auth');
                    }
                    return;
                  }
                  closeCreateSheet();
                  navigate('/travel');
                }}
              >
                {translateKey('navigation.myTravels') || '我的行程'}
              </button>

              {/* 只有管理员可以看到添加卡片按钮 */}
              {isAdmin && (
                <button
                  style={{ width: '100%', maxWidth: 520, padding: '14px 12px', borderRadius: 10, border: 'none', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 16, cursor: 'pointer' }}
                  onClick={() => {
                    
                    // require login before opening quick-add create flow
                    if (!currentUserId) {
                      
                      try { showError && showError(translateKey('navigation.loginToAddCard') || '請先登入以新增卡片'); } catch (e) {}
                      closeCreateSheet();
                      try {
                        const referer = (typeof window !== 'undefined') ? (window.location.pathname + window.location.search) : '/';
                        navigate(`/auth?referer=${encodeURIComponent(referer)}`);
                      } catch (e) {
                        navigate('/auth');
                      }
                      return;
                    }
                    
                    closeCreateSheet(); 
                    setShowQuickAdd(true);
                  }}
                >
                  {translateKey('navigation.addCard') || '添加卡片'}
                </button>
              )}
            </div>

            <div style={{ padding: 12 }}>
              <button
                style={{ width: '100%', padding: '12px 14px', borderRadius: 10, background: '#fff', border: '1px solid #E5E7EB' }}
                onClick={closeCreateSheet}
              >{translateKey('common.cancel') || '取消'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick-add radial menu (reused from module-travel) */}
      {showQuickAdd && (
        <QuickAddMenu
          visible={showQuickAdd}
          showBottomRight={false}
          itineraryMode={false}
          onClose={() => {
            
            setShowQuickAdd(false);
          }}
          onSelect={(type) => {
            
            // Close quick add and open either the full POI editor (for place-like types)
            // or the in-place CardFormHost for generic card types.
            setShowQuickAdd(false);
            try {
              if (type === 'poi' || type === 'activity' || type === 'lodging' || type === 'shopping' || type === 'meal' || type === 'restaurant' || type === 'video' || type === 'file') {
                // open full-screen POI-like editor prefilled with an empty payload
                let payload: any;
                if (type === 'poi') payload = createEmptyPoiPayload();
                else if (type === 'meal' || type === 'restaurant') payload = createEmptyMealPayload();
                else if (type === 'activity') payload = createEmptyActivityPayload();
                else if (type === 'lodging') payload = createEmptyLodgingPayload();
                else if (type === 'shopping') payload = createEmptyShoppingPayload();
                else if (type === 'video') payload = createEmptyVideoPayload();
                else if (type === 'file') payload = createEmptyExternalPayload();
                else payload = createEmptyPoiPayload(); // fallback
                setPoiPayload(payload);
                setShowPoiModal(true);
                return;
              }
            } catch (e) {
              // fall back to CardFormHost if payload helpers fail
              console.warn('QuickAdd payload helper failed, falling back to CardFormHost', e);
            }

            // default: open CardFormHost as before
            setCardFormType(type);
            setCardFormVisible(true);
          }}
        />
      )}

      {/* Full-screen POI-like editor (used for quick-add flows to match itinerary behavior) */}
      <FullscreenPoiModal
        visible={!!showPoiModal}
        initialEditMode={true}
        // When opened from the quick-add flow we want explicit Save/Cancel actions
        // so do not use the inline editable UI which hides the actions.
        editableInline={false}
        card={poiPayload && poiPayload.card ? poiPayload.card : undefined}
        title={poiPayload?.title}
        imageUrl={poiPayload?.imageUrl}
        address={poiPayload?.address}
        description={poiPayload?.description}
        metaData={poiPayload?.metaPairs}
        tags={poiPayload?.tags}
        rawData={poiPayload}
        supabaseClient={supabaseClient}
        currentUserId={currentUserId}
        onClose={() => {
          setShowPoiModal(false);
          setPoiPayload(null);
        }}
        // 不提供 onSave 回调，让 FullscreenPoiModal 使用内部保存逻辑保存到 cards_cards 表
        // Called by the modal after it loads/saves canonical DB row; use it to show toast & navigate
        onLoadedCanonical={(card: any) => {
            
          try { showSuccess && showSuccess('已儲存卡片'); } catch (e) {}
          // 关闭 modal 并清空状态
          setShowPoiModal(false);
          setPoiPayload(null);
          // 使用 setTimeout 确保状态更新后再导航
          setTimeout(() => {
            try { 
              const currentPath = window.location.pathname;
              // 如果已经在 /cards 页面，强制刷新
              if (currentPath === '/cards') {
                window.location.reload();
              } else {
                navigate('/cards');
              }
            } catch (err) {
              console.warn('[AppLayout] Navigation error:', err);
            }
          }, 100);
        }}
      />

      {/* CardFormHost used to create a card in-place when opened from the half-sheet */}
      <CardFormHost
        visible={cardFormVisible}
        cardType={cardFormType}
        onClose={() => {
          setCardFormVisible(false);
          setCardFormType(null);
        }}
        onSave={(data: any) => {
          // NOTE: persistence should be implemented by caller/service; here we
          // close the form and route user to their cards list as requested.
          setCardFormVisible(false);
          setCardFormType(null);
          try {
            navigate('/cards');
          } catch (err) {
            // ignore navigation errors
          }
        }}
      />

      {/* 固定底部导航栏 */}
      {showBottomNav && (Platform.OS === 'web' ? (
        // If we successfully created a portal container and loaded react-dom, portal the footer
        portalContainer && reactDom ? reactDom.createPortal(footerNode, portalContainer) : (
          // web fallback: render a native div with same sticky styles so safe-area works
          <div id="app-bottom-nav-fallback" style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2147483647,
            width: '100%',
            paddingBottom: 'calc(env(safe-area-inset-bottom, 16px) + 14px)',
            paddingTop: '10px',
            boxSizing: 'border-box',
            pointerEvents: footerHidden ? 'none' : 'auto',
            justifyContent: 'center',
            alignItems: 'center',
            transform: footerHidden ? 'translateY(110%)' : 'translateY(0)',
            transition: 'transform 240ms ease'
          }}>
            <div style={{ width: '100%', boxSizing: 'border-box' }}>
              <BottomTabBar
                items={webBottomItems}
                activeIndex={derivedActiveIndex}
                onPress={handleTabPress}
                showRaisedCenter
                onCenterPress={handleCenterPress}
              />
            </div>
          </div>
        )
      ) : (
        <View style={footerStyle}>
          <BottomTabBar
            items={bottomItems}
            activeIndex={derivedActiveIndex}
            onPress={handleTabPress}
            showRaisedCenter
            onCenterPress={handleCenterPress}
          />
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    overflow: 'visible', // allow sticky footer on web
  },
  content: {
    flex: 1,
    paddingBottom: 16,
    overflow: 'visible',
  },
  contentWeb: {
    paddingBottom: 80, // leave space for fixed bottom tab (reduced to avoid visible grey strip)
  },
  footer: {
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingHorizontal: 16,
    paddingTop: 6,
    paddingBottom: 8,
    overflow: 'visible',
  },
  footerWeb: {
  // Sticker-style footer for mobile browsers:
  // - fixed to viewport bottom
  // - respect safe-area inset (iOS notch / home indicator) via env()
  // - add a small extra offset to stay above gesture areas
  position: ('fixed' as any),
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 20000,
    width: '100%',
    // use CSS env for safe-area and a fallback (16px) if unavailable
    paddingBottom: `calc(env(safe-area-inset-bottom, 16px) + 8px)` as any,
    // provide a bit of top padding so the nav items aren't flush with the indicator
    paddingTop: 8,
  },
});

export default AppLayout;
